import {
    BehaviorSubject,
    combineLatest,
    distinctUntilChanged,
    filter,
    mergeMap,
    Observable,
    Subject,
    Subscription
} from "rxjs";
import {bindTo, BindToDirective} from "./bind-to.directive";
import {FieldProxy} from "./field.proxy";
import {Validator} from "./validator.abstract";
import {FormItem} from "./form-item.interface";
import {DirectiveResult} from "lit/directive.js";
import {ObjectHelper} from "../helpers/object.helper";

export class FormProxy<TValue extends Record<string, any>> implements FormItem<TValue, Partial<TValue>> {
    private fieldObservables$ = new Subject<Observable<any>>();
    private _fieldsSubscription!: Subscription;
    private readonly _fields = new Map<keyof TValue, FormItem<unknown>>();
    private readonly _changedValue$: BehaviorSubject<Partial<TValue>>;
    private readonly _reset$ = new Subject<void>();
    private readonly _commit$ = new Subject<void>();
    private readonly _validators: Validator[];

    public constructor(
        private readonly _key: string,
        fields: Record<string, FormItem<unknown>>,
        ...validators: Validator[]
    ) {
        this._fields = new Map(Object.entries(fields));

        this._validators = validators ?? [];

        this._changedValue$ = new BehaviorSubject(this.getCurrentValues(true));

        this._subscribeAllFieldChanges();

        const observables = Object.keys(fields).map(key => fields[key].changedValue$);
        combineLatest(observables).subscribe(() => this._changedValue$.next(this.getCurrentValues(true)));
    }

    get errors(): { [key: string]: string[] } {
        const errors = this._iterateFields(field => field.errors);

        return ObjectHelper.flatten(errors);
    }

    get hasChanged(): boolean {
        return Array.from(this._fields.values()).some(field => field.hasChanged);
    }

    get initialValue(): TValue {
        return this._iterateFields(field => field.initialValue) as TValue;
    }

    get isRequired(): boolean {
        return false;
    }

    get value(): TValue {
        return this._iterateFields(field => field.value) as TValue;
    }

    get key(): string {
        return this._key;
    }

    public get bindToFn(): (name: string) => DirectiveResult<typeof BindToDirective> {
        return this._bindTo.bind(this);
    }

    public get dirty(): boolean {
        return Array.from(this._fields.values()).some(field => field.dirty)
    }

    public get changedValue$(): Observable<Partial<TValue>> {
        return this._changedValue$.pipe(
            distinctUntilChanged(),
            filter(data => Object.entries(data).length > 0)
        );
    }

    public get reset$(): Observable<void> {
        return this._reset$.asObservable();
    }

    public get commit$(): Observable<void> {
        return this._commit$.asObservable();
    }

    public get valid(): boolean {
        return this._validators
                .every(validator => validator.isValid(this.getCurrentValues()) == null)
            && Object.values(
                this._iterateFields<boolean>(field => field.valid)
            ).every(value => value);
    }

    /**
     * The values of the fields that changed, keyed by field.
     */
    public getChangedValues(): { [key: string]: any } {
        return this.getCurrentValues(true);
    }

    /**
     * The values of all fields, or only of the changed ones, keyed by field.
     */
    public getCurrentValues(onlyChangedValues: boolean = false): Partial<TValue> {
        const values: any = {};

        for (const key of this._fields.keys()) {
            const field = this._fields.get(key)!;
            if (!onlyChangedValues || field.hasChanged) {
                Reflect.set(values, key, field.value);
            }
        }

        return values;
    }

    public getField<TValue = unknown>(key: string): FormItem<TValue> | null {
        return this._findFieldRecursive(key) as FormItem<TValue> | null;
    }

    public hasField(key: string): boolean {
        return this._findFieldRecursive(key) !== null;
    }

    public addField<T>(key: string, fieldProxy: FieldProxy<T>): void {
        if (this._fields.has(key)) {
            throw new Error(`A field with key «${key}» already exists`);
        }

        this._fields.set(key, fieldProxy);
        this.fieldObservables$.next(fieldProxy.changedValue$);
    }

    public removeField(key: string): void {
        if (!this._fields.has(key)) {
            throw new Error(`There is no field with key ${key} to remove.`);
        }

        this._fields.delete(key);
        this._unsubscribeAllFieldChanges();
        this._subscribeAllFieldChanges();
    }

    public reset(): void {
        if (this.dirty) {
            this._iterateFields(field => field.reset());
            this._reset$.next();
        }
    }

    public commit(): void {
        this._iterateFields(field => field.commit());
        this._commit$.next();
    }

    public clone(): FormItem<TValue, Partial<TValue>> {
        const fields: Record<string, FormItem<unknown>> = {};
        for (const key of this._fields.keys()) {
            Reflect.set(fields, key, this._fields.get(key)!.clone());
        }

        const validators: Validator[] = [];
        for (const validator of this._validators) {
            validators.push(Object.assign(Object.create(Object.getPrototypeOf(validator)), validator));
        }

        return new FormProxy<TValue>(this.key, fields, ...validators);
    }

    private _bindTo(key: string): DirectiveResult<typeof BindToDirective> {
        const field: FormItem<unknown> | null = this._findFieldRecursive(key);
        if (field == null) {
            throw new Error(`Cannot find field with key ${key}`);
        }

        return bindTo(field as any, key as any);
    }

    private _iterateFields<T>(callback: (field: FormItem<unknown>) => T): { [key: string]: T } {
        const result: { [key: string]: T } = {};
        for (const key of this._fields.keys()) {
            const field = this._fields.get(key)!;

            // noinspection TypeScriptValidateTypes
            const itemResult = callback.call(this, field);
            Reflect.set(result, key, itemResult);
        }

        return result;
    }

    private _subscribeAllFieldChanges(): void {
        this._fieldsSubscription = this.fieldObservables$.pipe(
            mergeMap(fieldObservable => fieldObservable)
        ).subscribe(() => this._changedValue$.next(this.getCurrentValues(true)));

        this._fields.forEach(field => this.fieldObservables$.next(field.changedValue$));
    }

    private _unsubscribeAllFieldChanges() {
        if (!!this._fieldsSubscription && !this._fieldsSubscription.closed) {
            this._fieldsSubscription.unsubscribe();
        }
    }

    private _findFieldRecursive<TKey extends keyof TValue>(key: TKey): FormItem<unknown> | null {
        const [fieldKey, nestedKey] = this._splitKey(key.toString());

        if (!this._fields.has(fieldKey)) {
            return null;
        }

        const formItem = this._fields.get(fieldKey!)! as FormItem<unknown>;

        if (nestedKey === null) {
            return formItem;
        }

        if (!(formItem instanceof FormProxy)) {
            throw new Error(`Requested form item is not a sub form.`);
        }

        return formItem._findFieldRecursive(nestedKey);
    }

    private _splitKey(key: string): [string, string | null] {
        const idx = key.indexOf(`.`);
        if (idx === -1) {
            return [key, null];
        }
        const fieldKey = key.substring(0, idx);
        const nestedKey = key.substring(idx + 1);
        return [fieldKey, nestedKey];
    }
}